import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  LinearProgress,
  Chip,
  Menu,
  Checkbox,
  ListItemText,
  Divider,
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import PaginacionControl from '../components/PaginacionControl';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/GetApp';
import FileDownloadIcon from '@mui/icons-material/GetApp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { GridOn } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import NoImageIcon from '@mui/icons-material/ImageNotSupported';
import ProductoImagenZoomDialog from '../components/ProductoImagenZoomDialog';
import { StockAlmacenesDialog } from '../components/ProductoBuscadorDialog';
import { useResizableCols } from '../hooks/useResizableCols';

// ─── Drag handle para resize de columnas ─────────────────────────────────────
const ResizeHandle = ({ onMouseDown }) => {
  const [hover, setHover] = useState(false);
  return (
    <span
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'absolute', right: 0, top: 0, bottom: 0, width: 8,
        cursor: 'col-resize', userSelect: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <span style={{
        width: hover ? 3 : 2, height: hover ? '70%' : '55%', borderRadius: 1,
        background: hover ? 'rgba(25,118,210,0.7)' : 'rgba(0,0,0,0.22)',
        pointerEvents: 'none', transition: 'all 0.12s',
      }} />
    </span>
  );
};

const PRODUCTOS_COL_DEFAULTS = {
  id: 60, codigo: 100, nombre: 220, categoria: 120, subcategoria: 120,
  marca: 100, modelo: 100, talla: 70, color: 70, unidad: 80,
  precio: 90, stock: 80, estado: 90, imagen: 80, observaciones: 200,
};

const PRODUCTOS_COLS_HIDDEN_KEY = 'productos-listado-cols-hidden';
// Marca de una sola vez: "observaciones" se agrega oculta por defecto para
// quienes ya tenian columnas guardadas antes de que existiera esta columna
// (si no, apareceria visible de golpe). Una vez aplicada, el usuario puede
// activarla desde el menu y se respeta su eleccion en las siguientes visitas.
const PRODUCTOS_COLS_OBS_MIGRATED_KEY = 'productos-listado-cols-obs-migrada';

const PRODUCTOS_COLS = [
  { key: 'id', label: 'ID', align: 'center', render: p => p.id },
  { key: 'codigo', label: 'Código', align: 'center', render: p => p.codigo },
  { key: 'nombre', label: 'Nombre', align: 'center', render: p => p.nombre },
  { key: 'categoria', label: 'Categoría', align: 'center', render: p => p.categoria_nombre },
  { key: 'subcategoria', label: 'Subcategoría', align: 'center', render: p => p.subcategoria_nombre },
  { key: 'marca', label: 'Marca', align: 'center', render: p => p.marca_nombre },
  { key: 'modelo', label: 'Modelo', align: 'center', render: p => p.modelo_nombre },
  { key: 'talla', label: 'Talla', align: 'center', render: p => p.talla_nombre },
  { key: 'color', label: 'Color', align: 'center', render: p => p.color_nombre },
  { key: 'unidad', label: 'Unidad', align: 'center', render: p => p.unidad_nombre },
  { key: 'precio', label: 'Precio', align: 'center', render: p => p.precio },
  {
    key: 'stock', label: 'Stock', align: 'center',
    render: p => (
      <Chip
        label={p.stock_total != null ? Number(p.stock_total).toFixed(0) : '0'}
        size="small"
        color={Number(p.stock_total) > 0 ? 'success' : 'default'}
        sx={{ fontSize: '0.7rem', height: 22 }}
      />
    ),
  },
  { key: 'estado', label: 'Estado', align: 'center', render: p => (p.estado === 1 || p.estado === true || p.estado === '1') ? 'ACTIVO' : 'INACTIVO' },
  { key: 'observaciones', label: 'Observaciones', align: 'left', render: p => p.observacion || '' },
  {
    key: 'imagen', label: 'Imagen', align: 'center',
    render: (p, ctx) => (
      <Box
        onDoubleClick={() => ctx.abrirZoom(p)}
        sx={{ cursor: 'zoom-in', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 40 }}
      >
        {p.imagen ? (
          <img
            src={p.imagen}
            alt={p.nombre}
            style={{ height: 40, width: 40, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }}
          />
        ) : (
          <Box sx={{
            height: 40, width: 40, borderRadius: 1, backgroundColor: '#f0f0f0',
            border: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'text.disabled',
          }}>
            <NoImageIcon sx={{ fontSize: 20, opacity: 0.5 }} />
          </Box>
        )}
      </Box>
    ),
  },
];

// Redimensiona/comprime una imagen en el navegador ANTES de enviarla — una
// foto de celular sin comprimir (a veces 8-15MB) supera el límite del body
// del servidor (10MB) y el guardado falla con "Payload Too Large".
function comprimirImagenEnNavegador(file, maxLado = 1200, calidad = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    reader.onload = (ev) => {
      const img = new Image();
      img.onerror = () => reject(new Error('No se pudo procesar la imagen'));
      img.onload = () => {
        let { width, height } = img;
        if (width > maxLado || height > maxLado) {
          if (width >= height) { height = Math.round(height * (maxLado / width)); width = maxLado; }
          else { width = Math.round(width * (maxLado / height)); height = maxLado; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', calidad));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });
}

const Productos = () => {
    // Maneja cambios en los formularios y subida de imagen
    const handleChange = (e) => {
      const { name, value, files } = e.target;
      if (name === 'imagen' && files && files[0]) {
        comprimirImagenEnNavegador(files[0])
          .then((dataUrl) => setForm(f => ({ ...f, imagen: dataUrl })))
          .catch(() => {
            // Si falla la compresion (formato raro, etc.), usar el archivo tal cual
            const reader = new FileReader();
            reader.onload = (ev) => setForm(f => ({ ...f, imagen: ev.target.result }));
            reader.readAsDataURL(files[0]);
          });
      } else {
        setForm({ ...form, [name]: value });
      }
    };
  const [productos, setProductos] = useState([]);
  // Estado para modal de imagen
  const [imgModal, setImgModal] = useState({ open: false, src: '', nombre: '', codigo: '' });
  const [zoomImg, setZoomImg] = useState({ open: false, src: '', nombre: '', codigo: '', precio: null, productoId: null });
  const abrirZoom = (p) => {
    setZoomImg({ open: true, src: p.imagen, nombre: p.nombre, codigo: p.codigo, precio: p.precio, productoId: p.id });
  };
  const [selectedRow, setSelectedRow] = useState(null);
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [hiddenCols, setHiddenCols] = useState(() => {
    try {
      const saved = localStorage.getItem(PRODUCTOS_COLS_HIDDEN_KEY);
      const lista = saved ? JSON.parse(saved) : [];
      // Primera vez que existe la columna "observaciones": se agrega oculta
      // sin pisar el resto de la preferencia ya guardada. Después de esto,
      // la marca queda puesta y no se vuelve a forzar (respeta si el usuario
      // la activa).
      if (!localStorage.getItem(PRODUCTOS_COLS_OBS_MIGRATED_KEY)) {
        try { localStorage.setItem(PRODUCTOS_COLS_OBS_MIGRATED_KEY, '1'); } catch {}
        if (!lista.includes('observaciones')) return [...lista, 'observaciones'];
      }
      return lista;
    } catch {}
    return ['observaciones'];
  });
  const [colMenu, setColMenu] = useState(null);
  const { widths: colW, startResize } = useResizableCols('productos-listado-cols', PRODUCTOS_COL_DEFAULTS);
  const visibleProductosCols = PRODUCTOS_COLS.filter(c => !hiddenCols.includes(c.key));

  const persistHiddenCols = (next) => {
    setHiddenCols(next);
    try { localStorage.setItem(PRODUCTOS_COLS_HIDDEN_KEY, JSON.stringify(next)); } catch {}
  };
  const handleColHeaderContextMenu = (e) => {
    e.preventDefault();
    setColMenu({ mouseX: e.clientX - 2, mouseY: e.clientY - 4 });
  };
  const toggleProductosCol = (key) => {
    const isHidden = hiddenCols.includes(key);
    if (!isHidden && PRODUCTOS_COLS.length - hiddenCols.length <= 1) return;
    persistHiddenCols(isHidden ? hiddenCols.filter(k => k !== key) : [...hiddenCols, key]);
  };
  const mostrarTodasProductosCols = () => {
    persistHiddenCols([]);
    setColMenu(null);
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'F1' && selectedRow) {
        e.preventDefault();
        setStockDialogOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedRow]);
  const [imgSize, setImgSize] = useState({ w: 500, h: 500 });
  const [imgSizeInput, setImgSizeInput] = useState({ w: '500', h: '500' });
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });
  const [downloadName, setDownloadName] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState(null);
  const [filtroMarca, setFiltroMarca] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [tallas, setTallas] = useState([]);
  const [colores, setColores] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    codigo: '', nombre: '', descripcion: '', observacion: '', categoria_id: '', subcategoria_id: '', marca_id: '', modelo_id: '', talla_id: '', color_id: '', unidad_medida_id: '', precio: '', stock_minimo: '', estado: 'ACTIVO', imagen: ''
  });

  // Estados para importar productos
  const [importDialog, setImportDialog]   = useState(false);
  const [importFile, setImportFile]       = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult]   = useState(null);
  const [importTipo, setImportTipo]       = useState('basico');
  const [imagenesDialog, setImagenesDialog] = useState(false);
  const [imagenesArchivos, setImagenesArchivos] = useState([]);
  const [imagenesLoading, setImagenesLoading] = useState(false);
  const [imagenesResult, setImagenesResult] = useState(null);
  const [imagenesDragOver, setImagenesDragOver] = useState(false);
  const [imagenesProgreso, setImagenesProgreso] = useState(null);

  const fetchProductos = async () => {
    try {
      const prod = await axios.get('/api/productos');
      setProductos(prod.data);
    } catch {}
  };

  const fetchAll = async () => {
    const [prod, cat, sub, mar, mod, tal, col, uni] = await Promise.all([
      axios.get('/api/productos'),
      axios.get('/api/categorias'),
      axios.get('/api/subcategorias'),
      axios.get('/api/marcas'),
      axios.get('/api/modelos'),
      axios.get('/api/tallas'),
      axios.get('/api/colores'),
      axios.get('/api/unidades')
    ]);
    setProductos(prod.data);
    setCategorias(cat.data);
    setSubcategorias(sub.data);
    setMarcas(mar.data);
    setModelos(mod.data);
    setTallas(tal.data);
    setColores(col.data);
    setUnidades(uni.data);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleOpen = (producto = null) => {
    if (producto) {
      setEditId(producto.id);
      setForm({
        codigo: producto.codigo || '',
        nombre: producto.nombre || '',
        descripcion: producto.descripcion || '',
        observacion: producto.observacion || '',
        categoria_id: producto.categoria_id || '',
        subcategoria_id: producto.subcategoria_id || '',
        marca_id: producto.marca_id || '',
        modelo_id: producto.modelo_id || '',
        talla_id: producto.talla_id || '',
        color_id: producto.color_id || '',
        unidad_medida_id: producto.unidad_medida_id || '',
        precio: producto.precio || '',
        stock_minimo: producto.stock_minimo != null ? producto.stock_minimo : '',
        estado: (producto.estado === 1 || producto.estado === true || producto.estado === '1') ? 'ACTIVO' : 'INACTIVO',
        imagen: producto.imagen || ''
      });
    } else {
      setEditId(null);
      setForm({ codigo: '', nombre: '', descripcion: '', observacion: '', categoria_id: '', subcategoria_id: '', marca_id: '', modelo_id: '', talla_id: '', color_id: '', unidad_medida_id: '', precio: '', stock_minimo: '', estado: 'ACTIVO', imagen: '' });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    setForm({ codigo: '', nombre: '', descripcion: '', observacion: '', categoria_id: '', subcategoria_id: '', marca_id: '', modelo_id: '', talla_id: '', color_id: '', unidad_medida_id: '', precio: '', stock_minimo: '', estado: 'ACTIVO', imagen: '' });
  };

  const handleSave = async () => {
    if (!form.nombre) return;
    // Convertir estado a bit
    const formToSend = {
      ...form,
      estado: form.estado === 'ACTIVO' ? 1 : 0
    };
    try {
      if (editId) {
        await axios.put(`/api/productos/${editId}`, formToSend);
      } else {
        await axios.post('/api/productos', formToSend);
      }
      fetchAll();
      handleClose();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar producto');
    }
  };

  const handleDelete = (id) => {
    setConfirmDelete({ open: true, id });
  };

  const handleConfirmDelete = async () => {
    const id = confirmDelete.id;
    setConfirmDelete({ open: false, id: null });
    try {
      await axios.delete(`/api/productos/${id}`);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar producto');
    }
  };

  // Descargar plantilla Excel para importación
  const downloadPlantilla = async () => {
    const ExcelJS = (await import('exceljs')).default;
    const { saveAs } = await import('file-saver');
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Productos');
    ws.columns = [
      { header: 'Nombre', key: 'nombre', width: 30 },
      { header: 'Descripcion', key: 'descripcion', width: 30 },
      { header: 'Codigo', key: 'codigo', width: 15 },
      { header: 'Precio Venta', key: 'precio', width: 14 },
      { header: 'Costo', key: 'costo', width: 14 },
      { header: 'categoria_id', key: 'categoria_id', width: 14 },
      { header: 'unidad_id', key: 'unidad_id', width: 12 },
      { header: 'Stock Inicial', key: 'stock_inicial', width: 14 },
      { header: 'Stock Minimo', key: 'stock_minimo', width: 14 },
      { header: 'almacen_id', key: 'almacen_id', width: 12 },
      { header: 'sucursal_id', key: 'sucursal_id', width: 12 },
    ];
    // Style header row
    const hdr = ws.getRow(1);
    hdr.height = 22;
    hdr.eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0D47A1' } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });
    // Example row
    ws.addRow({
      nombre: 'Producto ejemplo', descripcion: 'Descripción opcional', codigo: 'PROD-001',
      precio: 15.00, costo: 8.00, categoria_id: 1, unidad_id: 1,
      stock_inicial: 50, stock_minimo: 5, almacen_id: 1, sucursal_id: 1
    });
    // Instructions sheet
    const ws2 = wb.addWorksheet('Instrucciones');
    ws2.getCell('A1').value = 'INSTRUCCIONES DE IMPORTACIÓN';
    ws2.getCell('A1').font = { bold: true, size: 13 };
    const instrucciones = [
      ['Campo', 'Requerido', 'Descripción'],
      ['Nombre', 'SÍ', 'Nombre del producto (único)'],
      ['Descripcion', 'No', 'Descripción adicional'],
      ['Codigo', 'No', 'Código interno del producto'],
      ['Precio Venta', 'No', 'Precio de venta (default 0)'],
      ['Costo', 'No', 'Costo del producto para kardex'],
      ['categoria_id', 'No', 'ID de categoría (ver catálogo)'],
      ['unidad_id', 'No', 'ID de unidad (ver catálogo)'],
      ['Stock Inicial', 'No', 'Cantidad inicial en almacén (se registra en kardex)'],
      ['Stock Minimo', 'No', 'Stock mínimo para alerta de notificaciones (dejar vacío = sin alerta)'],
      ['almacen_id', 'No', 'ID del almacén para el stock inicial (default 1)'],
      ['sucursal_id', 'No', 'ID de la sucursal (default 1)'],
    ];
    instrucciones.forEach((row, i) => {
      const r = ws2.addRow(row);
      if (i === 0) { r.font = { bold: true }; r.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3F2FD' } }; }
    });
    ws2.columns = [{ width: 18 }, { width: 12 }, { width: 55 }];

    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), 'Plantilla_Importacion_Productos.xlsx');
  };

  // Descargar Plantilla Completa (categoría/marca/talla por nombre)
  const downloadPlantillaCompleta = async () => {
    const ExcelJS = (await import('exceljs')).default;
    const { saveAs } = await import('file-saver');
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Productos');
    ws.columns = [
      { header: 'codigo',        key: 'codigo',        width: 16 },
      { header: 'nombre',        key: 'nombre',        width: 30 },
      { header: 'descripcion',   key: 'descripcion',   width: 30 },
      { header: 'categoria',     key: 'categoria',     width: 20 },
      { header: 'subcategoria',  key: 'subcategoria',  width: 20 },
      { header: 'marca',         key: 'marca',         width: 18 },
      { header: 'modelo',        key: 'modelo',        width: 18 },
      { header: 'talla',         key: 'talla',         width: 12 },
      { header: 'color',         key: 'color',         width: 14 },
      { header: 'unidad_medida', key: 'unidad_medida', width: 14 },
      { header: 'precio_venta',  key: 'precio_venta',  width: 14 },
      { header: 'stock_minimo',  key: 'stock_minimo',  width: 14 },
      { header: 'afecta_kardex', key: 'afecta_kardex', width: 14 },
    ];
    const hdr = ws.getRow(1);
    hdr.height = 22;
    hdr.eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1B5E20' } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });
    ws.addRow({ codigo: 'PROD-001', nombre: 'Producto ejemplo', descripcion: 'Descripción opcional',
      categoria: 'Electrónica', subcategoria: 'Accesorios', marca: 'Samsung', modelo: 'Galaxy',
      talla: '', color: 'Negro', unidad_medida: 'NIU', precio_venta: 99.90, stock_minimo: 3, afecta_kardex: 1 });
    const ws2 = wb.addWorksheet('Instrucciones');
    ws2.getCell('A1').value = 'INSTRUCCIONES — PLANTILLA COMPLETA (CATEGORÍA/MARCA/TALLA)';
    ws2.getCell('A1').font = { bold: true, size: 13 };
    [
      ['Campo',        'Requerido', 'Descripción'],
      ['codigo',       'No',  'Código interno del producto'],
      ['nombre',       'SÍ',  'Nombre del producto (único)'],
      ['descripcion',  'No',  'Descripción adicional'],
      ['categoria',    'No',  'Nombre de categoría — se crea automáticamente si no existe'],
      ['subcategoria', 'No',  'Nombre de subcategoría — vinculada a la categoría de la misma fila'],
      ['marca',        'No',  'Nombre de marca — se crea automáticamente si no existe'],
      ['modelo',       'No',  'Nombre de modelo — vinculado a la marca de la misma fila'],
      ['talla',        'No',  'Nombre de talla — se crea automáticamente si no existe'],
      ['color',        'No',  'Nombre de color — se crea automáticamente si no existe'],
      ['unidad_medida','No',  'Abreviatura de unidad (NIU, KGM, UND...) — se crea si no existe'],
      ['precio_venta', 'No',  'Precio de venta (default 0)'],
      ['stock_minimo', 'No',  'Stock mínimo para alerta de notificaciones (vacío = sin alerta)'],
      ['afecta_kardex','No',  '1 = afecta inventario al crear categoría nueva | 0 = no afecta'],
    ].forEach((row, i) => {
      const r = ws2.addRow(row);
      if (i === 0) { r.font = { bold: true }; r.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } }; }
    });
    ws2.columns = [{ width: 18 }, { width: 12 }, { width: 65 }];
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), 'Plantilla_Completa_Productos.xlsx');
    setImportTipo('avanzado');
  };

  // Importar productos desde archivo Excel
  const handleImport = async () => {
    if (!importFile) return;
    setImportLoading(true);
    const endpoint = importTipo === 'avanzado'
      ? '/api/productos/importar-excel-avanzado'
      : '/api/productos/importar-excel';
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(res.data);
      if (res.data.creados > 0) fetchProductos();
    } catch (err) {
      setImportResult({ ok: false, error: err.response?.data?.error || err.message });
    }
    setImportLoading(false);
  };

  // ── Carga masiva de imágenes por código de producto ──────────────────────
  const esImagen = (file) => /^image\//.test(file.type) || /\.(jpe?g|png|bmp|gif|webp)$/i.test(file.name);

  // Lee recursivamente una carpeta arrastrada (drag & drop) usando la File System Entries API.
  // readEntries() no devuelve todo de una vez (Chrome lo topa en ~100 por llamada):
  // hay que seguir llamándolo hasta que devuelva un lote vacío para tener el listado completo.
  const leerEntradaRecursiva = (entry) => new Promise((resolve) => {
    if (entry.isFile) {
      entry.file(file => resolve([file]), () => resolve([]));
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      const todasEntradas = [];
      const leerLote = () => {
        reader.readEntries(async (entries) => {
          if (!entries.length) {
            const listas = await Promise.all(todasEntradas.map(leerEntradaRecursiva));
            resolve(listas.flat());
          } else {
            todasEntradas.push(...entries);
            leerLote();
          }
        }, () => resolve([]));
      };
      leerLote();
    } else {
      resolve([]);
    }
  });

  const handleImagenesDrop = async (e) => {
    e.preventDefault();
    setImagenesDragOver(false);
    const items = e.dataTransfer.items;
    let archivos = [];
    if (items && items.length && items[0].webkitGetAsEntry) {
      const entradas = Array.from(items).map(it => it.webkitGetAsEntry()).filter(Boolean);
      const listas = await Promise.all(entradas.map(leerEntradaRecursiva));
      archivos = listas.flat();
    } else {
      archivos = Array.from(e.dataTransfer.files);
    }
    agregarImagenesSeleccionadas(archivos);
  };

  const agregarImagenesSeleccionadas = (archivos) => {
    const soloImagenes = archivos.filter(esImagen);
    setImagenesArchivos(prev => [...prev, ...soloImagenes]);
    setImagenesResult(null);
  };

  const IMAGENES_LOTE_SIZE = 100;

  const handleImagenesUpload = async () => {
    if (!imagenesArchivos.length) return;
    setImagenesLoading(true);
    setImagenesResult(null);

    const lotes = [];
    for (let i = 0; i < imagenesArchivos.length; i += IMAGENES_LOTE_SIZE) {
      lotes.push(imagenesArchivos.slice(i, i + IMAGENES_LOTE_SIZE));
    }

    const acumulado = { total: 0, asignados: 0, noEncontrados: [], fallidos: [] };
    let huboExito = false;

    for (let i = 0; i < lotes.length; i++) {
      setImagenesProgreso({ loteActual: i + 1, totalLotes: lotes.length, procesadas: acumulado.total, totalArchivos: imagenesArchivos.length });
      try {
        const formData = new FormData();
        lotes[i].forEach(f => formData.append('imagenes', f));
        const res = await axios.post('/api/productos/importar-imagenes', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        acumulado.total += res.data.total || 0;
        acumulado.asignados += res.data.asignados || 0;
        acumulado.noEncontrados.push(...(res.data.noEncontrados || []));
        acumulado.fallidos.push(...(res.data.fallidos || []));
        huboExito = true;
      } catch (err) {
        acumulado.fallidos.push({ codigo: `[Lote ${i + 1}]`, error: err.response?.data?.error || err.message });
      }
      setImagenesResult({ ok: true, ...acumulado });
    }

    setImagenesProgreso(null);
    if (huboExito && acumulado.asignados > 0) fetchProductos();
    setImagenesArchivos([]);
    setImagenesLoading(false);
  };

  // Filtrado y paginación: buscar en todas las columnas principales
  const filtered = productos.filter(p => {
    const search = searchValue.toLowerCase();
    const matchTexto = (
      (p.id || '').toString().toLowerCase().includes(search) ||
      (p.codigo || '').toString().toLowerCase().includes(search) ||
      (p.nombre || '').toString().toLowerCase().includes(search) ||
      (p.categoria_nombre || '').toString().toLowerCase().includes(search) ||
      (p.subcategoria_nombre || '').toString().toLowerCase().includes(search) ||
      (p.marca_nombre || '').toString().toLowerCase().includes(search) ||
      (p.modelo_nombre || '').toString().toLowerCase().includes(search) ||
      (p.talla_nombre || '').toString().toLowerCase().includes(search) ||
      (p.color_nombre || '').toString().toLowerCase().includes(search) ||
      (p.unidad_nombre || '').toString().toLowerCase().includes(search) ||
      (p.precio || '').toString().toLowerCase().includes(search) ||
      (p.estado === 1 || p.estado === true || p.estado === '1' ? 'activo' : 'inactivo').includes(search)
    );
    const matchCategoria = !filtroCategoria || p.categoria_id === filtroCategoria.id;
    const matchMarca = !filtroMarca || p.marca_id === filtroMarca.id;
    return matchTexto && matchCategoria && matchMarca;
  });
  const paginated = rowsPerPage === -1 ? filtered : filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Exportar a Excel
  const handleExportExcel = async () => {
    const { exportGenericWorkbook } = await import('../utils/excel/genericWorkbook');
    const rows = filtered.map(p => ({
      id: p.id,
      codigo: p.codigo,
      nombre: p.nombre,
      categoria: p.categoria_nombre,
      subcategoria: p.subcategoria_nombre,
      marca: p.marca_nombre,
      modelo: p.modelo_nombre,
      talla: p.talla_nombre,
      color: p.color_nombre,
      unidad: p.unidad_nombre,
      precio: p.precio,
      stock_minimo: p.stock_minimo != null ? p.stock_minimo : '',
      estadoStr: (p.estado === 1 || p.estado === true || p.estado === '1') ? 'ACTIVO' : 'INACTIVO'
    }));
    await exportGenericWorkbook({
      titulo: 'REPORTE - PRODUCTOS',
      sheetName: 'Productos',
      tableName: 'tblProductos',
      fileName: 'Productos',
      columns: [
        { key: 'id', label: 'ID', width: 8 },
        { key: 'codigo', label: 'Código', width: 16 },
        { key: 'nombre', label: 'Nombre', width: 30 },
        { key: 'categoria', label: 'Categoría', width: 20 },
        { key: 'subcategoria', label: 'Subcategoría', width: 20 },
        { key: 'marca', label: 'Marca', width: 18 },
        { key: 'modelo', label: 'Modelo', width: 18 },
        { key: 'talla', label: 'Talla', width: 12 },
        { key: 'color', label: 'Color', width: 14 },
        { key: 'unidad', label: 'Unidad', width: 14 },
        { key: 'precio', label: 'Precio', width: 14, sumTotal: true },
        { key: 'stock_minimo', label: 'Stock Mínimo', width: 14 },
        { key: 'estadoStr', label: 'Estado', width: 14, isEstado: true }
      ],
      rows,
      searchText: searchValue,
      usuario: ''
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1800 }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: { xs: 'center', md: 'left' }, mb: 3, fontWeight: 'bold', color: '#222' }}>Gestión de Productos</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Autocomplete
            size="small"
            options={categorias}
            getOptionLabel={option => option?.nombre || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={filtroCategoria}
            onChange={(_, value) => { setFiltroCategoria(value); setPage(0); }}
            renderInput={params => <TextField {...params} label="Categoría" />}
            sx={{ minWidth: 170 }}
          />
          <Autocomplete
            size="small"
            options={marcas}
            getOptionLabel={option => option?.nombre || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={filtroMarca}
            onChange={(_, value) => { setFiltroMarca(value); setPage(0); }}
            renderInput={params => <TextField {...params} label="Marca" />}
            sx={{ minWidth: 170 }}
          />
          <TextField
            size="small"
            placeholder="Buscar..."
            value={searchValue}
            onChange={e => { setSearchValue(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <Box sx={{ color: 'text.secondary', mr: 1 }}><SearchIcon /></Box> }}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <Button
            variant="contained"
            color="success"
            startIcon={<GridOn />}
            onClick={handleExportExcel}
            sx={{ minWidth: 120 }}
          >Exportar Excel</Button>
          <Button variant="outlined" size="small" color="secondary"
            startIcon={<CloudUploadIcon />}
            onClick={() => { setImportDialog(true); setImportFile(null); setImportResult(null); }}
          >
            Importar Excel
          </Button>
          <Button variant="outlined" size="small" color="info"
            startIcon={<CloudUploadIcon />}
            onClick={() => { setImagenesDialog(true); setImagenesArchivos([]); setImagenesResult(null); }}
          >
            Cargar Imágenes
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()} sx={{ ml: { xs: 0, sm: 'auto' }, mt: { xs: 2, sm: 0 } }}>
            Nuevo Producto
          </Button>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Mostrar</InputLabel>
            <Select value={rowsPerPage} label="Mostrar" onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={-1}>Todos</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          F1 = ver stock por almacén (selecciona una fila primero) · Doble clic en Imagen = ver/ampliar · Clic derecho en el encabezado = mostrar/ocultar columnas
        </Typography>
        <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mt: 1, overflowX: 'auto' }}>
        <Table sx={{ tableLayout: 'fixed', minWidth: visibleProductosCols.reduce((s, c) => s + (colW[c.key] || 80), 0) + 160 }}>
          <colgroup>
            {visibleProductosCols.map(c => <col key={c.key} style={{ width: colW[c.key] }} />)}
            <col style={{ width: 160 }} />
          </colgroup>
          <TableHead>
            <TableRow onContextMenu={handleColHeaderContextMenu}>
              {visibleProductosCols.map(col => (
                <TableCell key={col.key} align="center" sx={{ position: 'relative', userSelect: 'none', cursor: 'context-menu' }}>
                  {col.label}
                  <ResizeHandle onMouseDown={e => startResize(col.key, e)} />
                </TableCell>
              ))}
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.map((p) => {
              const isSel = selectedRow?.id === p.id;
              return (
              <TableRow
                key={p.id}
                onClick={() => setSelectedRow(p)}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: isSel ? '#e3f2fd' : undefined,
                  '&:hover': { backgroundColor: isSel ? '#e3f2fd' : '#fafafa' },
                }}
              >
                {visibleProductosCols.map(col => (
                  <TableCell key={col.key} align={col.align || 'center'} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {col.render(p, { abrirZoom })}
                  </TableCell>
                ))}
                <TableCell align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                    <IconButton color="primary" onClick={(e) => { e.stopPropagation(); handleOpen(p); }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton color="error" onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }}>
                      <DeleteIcon />
                    </IconButton>
                    {p.imagen && (
                      <IconButton
                        color="success"
                        onClick={(e) => {
                          e.stopPropagation();
                          const link = document.createElement('a');
                          link.href = p.imagen;
                          link.download = (p.id ? p.id : 'imagen_producto') + '.png';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        title="Descargar imagen"
                      >
                        <DownloadIcon />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Menu
          open={!!colMenu}
          onClose={() => setColMenu(null)}
          anchorReference="anchorPosition"
          anchorPosition={colMenu ? { top: colMenu.mouseY, left: colMenu.mouseX } : undefined}
        >
          <MenuItem dense onClick={mostrarTodasProductosCols} disabled={hiddenCols.length === 0}>
            <ListItemText>Mostrar todas</ListItemText>
          </MenuItem>
          <Divider />
          {PRODUCTOS_COLS.map(col => (
            <MenuItem key={col.key} dense onClick={() => toggleProductosCol(col.key)} sx={{ py: 0.25 }}>
              <Checkbox size="small" checked={!hiddenCols.includes(col.key)} sx={{ p: 0.5 }} />
              <ListItemText primary={col.label} />
            </MenuItem>
          ))}
        </Menu>
      </TableContainer>

      <StockAlmacenesDialog
        open={stockDialogOpen}
        onClose={() => setStockDialogOpen(false)}
        productoId={selectedRow?.id}
        productoNombre={selectedRow?.nombre}
      />
        <PaginacionControl page={page} rowsPerPage={rowsPerPage} count={filtered.length} onPageChange={setPage} />
      <Dialog open={open} onClose={(_, r) => r !== 'backdropClick' && handleClose()} disableEscapeKeyDown={false} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            {editId ? 'Editar Producto' : 'Nuevo Producto'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <TextField
              margin="dense"
              label="Código"
              name="codigo"
              value={form.codigo}
              onChange={handleChange}
              sx={{ flex: { xs: 'unset', sm: 2 }, minWidth: 0 }}
              fullWidth
            />
            <TextField
              margin="dense"
              label="Nombre"
              name="nombre"
              value={form.nombre}
              onChange={handleChange}
              sx={{ flex: { xs: 'unset', sm: 4 }, minWidth: 0 }}
              fullWidth
            />
            <TextField
              margin="dense"
              label="Descripción"
              name="descripcion"
              value={form.descripcion}
              onChange={handleChange}
              sx={{ flex: { xs: 'unset', sm: 4 }, minWidth: 0 }}
              fullWidth
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <TextField
              margin="dense"
              label="Observación"
              name="observacion"
              value={form.observacion}
              onChange={handleChange}
              multiline
              minRows={2}
              sx={{ flex: 1, minWidth: 0 }}
              fullWidth
            />
          </Box>
          {/* Fila: Categoría y Subcategoría */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <Autocomplete
              options={categorias}
              getOptionLabel={option => option?.nombre || ''}
              value={categorias.find(c => c.id === form.categoria_id) || null}
              onChange={(_, value) => setForm(f => ({ ...f, categoria_id: value ? value.id : '' }))}
              renderInput={params => <TextField {...params} label="Categoría" margin="dense" />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ flex: 1 }}
            />
            <Autocomplete
              options={subcategorias}
              getOptionLabel={option => option?.nombre || ''}
              value={subcategorias.find(sc => sc.id === form.subcategoria_id) || null}
              onChange={(_, value) => setForm(f => ({ ...f, subcategoria_id: value ? value.id : '' }))}
              renderInput={params => <TextField {...params} label="Subcategoría" margin="dense" />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Fila: Marca y Modelo */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <Autocomplete
              options={marcas}
              getOptionLabel={option => option?.nombre || ''}
              value={marcas.find(m => m.id === form.marca_id) || null}
              onChange={(_, value) => setForm(f => ({ ...f, marca_id: value ? value.id : '' }))}
              renderInput={params => <TextField {...params} label="Marca" margin="dense" />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ flex: 1 }}
            />
            <Autocomplete
              options={modelos}
              getOptionLabel={option => option?.nombre || ''}
              value={modelos.find(mo => mo.id === form.modelo_id) || null}
              onChange={(_, value) => setForm(f => ({ ...f, modelo_id: value ? value.id : '' }))}
              renderInput={params => <TextField {...params} label="Modelo" margin="dense" />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Fila: Talla y Color */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <Autocomplete
              options={tallas}
              getOptionLabel={option => option?.nombre || ''}
              value={tallas.find(t => t.id === form.talla_id) || null}
              onChange={(_, value) => setForm(f => ({ ...f, talla_id: value ? value.id : '' }))}
              renderInput={params => <TextField {...params} label="Talla" margin="dense" />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ flex: 1 }}
            />
            <Autocomplete
              options={colores}
              getOptionLabel={option => option?.nombre || ''}
              value={colores.find(c => c.id === form.color_id) || null}
              onChange={(_, value) => setForm(f => ({ ...f, color_id: value ? value.id : '' }))}
              renderInput={params => <TextField {...params} label="Color" margin="dense" />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Fila: Unidad, Precio, Estado, Imagen */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
            <Autocomplete
              options={unidades}
              getOptionLabel={option => option?.nombre ? `${option.nombre} (${option.abreviatura})` : ''}
              value={unidades.find(u => u.id === form.unidad_medida_id) || null}
              onChange={(_, value) => setForm(f => ({ ...f, unidad_medida_id: value ? value.id : '' }))}
              renderInput={params => <TextField {...params} label="Unidad" margin="dense" />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ flex: 3, minWidth: 120 }}
            />
            <TextField
              margin="dense"
              label="Precio"
              name="precio"
              value={form.precio}
              onChange={handleChange}
              type="number"
              sx={{ flex: 2, minWidth: 80 }}
            />
            <TextField
              margin="dense"
              label="Stock Mínimo"
              name="stock_minimo"
              value={form.stock_minimo}
              onChange={handleChange}
              type="number"
              inputProps={{ step: '0.0001', min: '0' }}
              placeholder="Sin alerta"
              sx={{ flex: 2, minWidth: 90 }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 3, minWidth: 120, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                <Typography variant="body1" sx={{ mr: 1 }}>Estado:</Typography>
                <Switch
                  checked={form.estado === 'ACTIVO'}
                  onChange={e => setForm(f => ({ ...f, estado: e.target.checked ? 'ACTIVO' : 'INACTIVO' }))}
                  color="success"
                  inputProps={{ 'aria-label': 'switch-estado' }}
                />
                <Typography variant="body2" color={form.estado === 'ACTIVO' ? 'success.main' : 'text.secondary'}>
                  {form.estado === 'ACTIVO' ? 'Activo' : 'Inactivo'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flex: 2, minWidth: 120, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <Button
                variant="contained"
                color="info"
                component="label"
                startIcon={<CloudUploadIcon />}
                sx={{ minWidth: 0, px: 2, whiteSpace: 'nowrap' }}
              >
                Subir Imagen
                <input type="file" name="imagen" accept="image/*" hidden onChange={handleChange} />
              </Button>
              {form.imagen && (
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                  <img src={form.imagen} alt="Producto" style={{ maxHeight: 80, borderRadius: 4, border: '1px solid #ddd', cursor: 'pointer' }}
                    onClick={() => {
                      setImgModal({ open: true, src: form.imagen, nombre: form.nombre, id: editId });
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} startIcon={<CloseIcon />} color="error" variant="contained">
            Cancelar
          </Button>
          <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />} color="primary">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación de eliminación */}
      <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, id: null })}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>¿Está seguro de que desea eliminar este producto?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ open: false, id: null })}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete}>Eliminar</Button>
        </DialogActions>
      </Dialog>

      {/* Popup de zoom con código, precio y stock por almacén (doble clic en la imagen del listado) */}
      <ProductoImagenZoomDialog
        open={zoomImg.open}
        onClose={() => setZoomImg({ open: false, src: '', nombre: '', codigo: '', precio: null, productoId: null })}
        src={zoomImg.src}
        nombre={zoomImg.nombre}
        codigo={zoomImg.codigo}
        precio={zoomImg.precio}
        productoId={zoomImg.productoId}
      />

      {/* Modal visor de imagen (usado en el formulario de crear/editar, antes de guardar) */}
      <Dialog open={imgModal.open} onClose={() => setImgModal({ open: false, src: '', nombre: '', id: '' })} maxWidth="lg" fullWidth PaperProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.88)' } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          {/* Controles de tamaño */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2 }}>
            <TextField
              label="Ancho (px)"
              value={imgSizeInput.w}
              onChange={e => {
                const raw = e.target.value.replace(/[^0-9]/g, '');
                setImgSizeInput(s => ({ ...s, w: raw }));
                const v = parseInt(raw);
                if (v >= 50) setImgSize(s => ({ ...s, w: v }));
              }}
              onBlur={() => {
                const v = parseInt(imgSizeInput.w);
                if (!(v >= 50)) { setImgSize(s => ({ ...s, w: 500 })); setImgSizeInput(s => ({ ...s, w: '500' })); }
              }}
              onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
              size="small"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              sx={{ width: 130, '& .MuiInputLabel-root': { color: '#ccc' }, '& .MuiInputBase-input': { color: '#fff' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#666' } }}
            />
            <Typography color="#aaa" sx={{ fontSize: 18, lineHeight: 1 }}>×</Typography>
            <TextField
              label="Alto (px)"
              value={imgSizeInput.h}
              onChange={e => {
                const raw = e.target.value.replace(/[^0-9]/g, '');
                setImgSizeInput(s => ({ ...s, h: raw }));
                const v = parseInt(raw);
                if (v >= 50) setImgSize(s => ({ ...s, h: v }));
              }}
              onBlur={() => {
                const v = parseInt(imgSizeInput.h);
                if (!(v >= 50)) { setImgSize(s => ({ ...s, h: 500 })); setImgSizeInput(s => ({ ...s, h: '500' })); }
              }}
              onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
              size="small"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              sx={{ width: 130, '& .MuiInputLabel-root': { color: '#ccc' }, '& .MuiInputBase-input': { color: '#fff' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#666' } }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={() => { setImgSize({ w: 500, h: 500 }); setImgSizeInput({ w: '500', h: '500' }); }}
              sx={{ color: '#aaa', borderColor: '#555', fontSize: 11, py: 0.5, px: 1, minWidth: 'unset' }}
            >Reset</Button>
          </Box>
          <img
            src={imgModal.src}
            alt={imgModal.nombre}
            style={{ width: imgSize.w, height: imgSize.h, objectFit: 'contain', borderRadius: 8, boxShadow: '0 4px 24px #0008', background: '#111' }}
          />
          <Typography variant="h6" color="white" sx={{ mt: 2 }}>{imgModal.nombre}</Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button variant="contained" color="secondary" onClick={() => setImgModal({ open: false, src: '', nombre: '', id: '' })}>Cerrar</Button>
            <Button variant="outlined" color="success" onClick={() => {
              const link = document.createElement('a');
              link.href = imgModal.src;
              link.download = (imgModal.id ? imgModal.id : 'imagen_producto') + '.png';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}>Descargar</Button>
          </Box>
        </Box>
      </Dialog>

      {/* Dialog Importar Productos */}
      <Dialog open={importDialog} onClose={() => setImportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 700 }}>
          Importar Productos desde Excel
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>1. Descarga la plantilla correspondiente:</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Button variant={importTipo === 'basico' ? 'contained' : 'outlined'} size="small" color="info"
                onClick={() => { downloadPlantilla(); setImportTipo('basico'); }}>
                Plantilla Básica (IDs)
              </Button>
              <Button variant={importTipo === 'avanzado' ? 'contained' : 'outlined'} size="small" color="success"
                onClick={downloadPlantillaCompleta}>
                Plantilla Completa (Categoría/Marca…)
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {importTipo === 'avanzado'
                ? 'Plantilla Completa: escribe nombres de categoría, marca, talla, etc. Se crean automáticamente si no existen.'
                : 'Plantilla Básica: usa IDs numéricos de categoría y unidad. Incluye Stock Inicial en Kardex.'}
            </Typography>
          </Box>
          <Box sx={{ border: '2px dashed', borderColor: 'primary.light', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>2. Sube el archivo completado:</Typography>
            <input type="file" accept=".xlsx,.xls" id="import-file-input" style={{ display: 'none' }}
              onChange={async e => {
                const file = e.target.files[0];
                if (!file) return;
                setImportFile(file);
                setImportResult(null);
                // Auto-detectar si el Excel es formato avanzado (categoria por nombre) o básico (categoria_id)
                try {
                  const ExcelJS = (await import('exceljs')).default;
                  const wb = new ExcelJS.Workbook();
                  await wb.xlsx.load(await file.arrayBuffer());
                  const ws = wb.worksheets[0];
                  if (ws) {
                    const hdrs = [];
                    ws.getRow(1).eachCell(cell => { const t = (cell.text || '').trim().toLowerCase(); if (t) hdrs.push(t); });
                    const esAvanzado = hdrs.includes('categoria') || hdrs.includes('precio_venta');
                    setImportTipo(esAvanzado ? 'avanzado' : 'basico');
                  }
                } catch (_) {}
              }} />
            <label htmlFor="import-file-input">
              <Button variant="contained" component="span" startIcon={<CloudUploadIcon />}>
                Seleccionar archivo Excel
              </Button>
            </label>
            {importFile && (
              <Typography variant="body2" sx={{ mt: 1, color: 'success.dark', fontWeight: 600 }}>
                {importFile.name}
              </Typography>
            )}
          </Box>
          {importResult && (
            <Box sx={{ mt: 2, p: 2, bgcolor: importResult.ok ? 'success.light' : 'error.light', borderRadius: 1 }}>
              {importResult.ok ? (
                <>
                  <Typography fontWeight={700} color="success.dark">Importación completada</Typography>
                  <Typography variant="body2">Productos creados: <b>{importResult.creados}</b></Typography>
                  {importResult.actualizados > 0 && (
                    <Typography variant="body2">Productos actualizados: <b>{importResult.actualizados}</b></Typography>
                  )}
                  {importResult._headersDetectados && (
                    <Typography variant="body2" sx={{ mt: 1, fontSize: '0.72rem', color: 'text.secondary' }}>
                      Headers detectados: <b>{importResult._headersDetectados.join(', ')}</b>
                    </Typography>
                  )}
                  {importResult.erroresDetalle?.length > 0 && (
                    <Typography variant="body2" color="error.dark">
                      Errores: {importResult.erroresDetalle.join(' | ')}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography color="error.dark">Error: {importResult.error}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialog(false)}>Cerrar</Button>
          <Button variant="contained" onClick={handleImport}
            disabled={!importFile || importLoading}
            startIcon={importLoading ? null : <CloudUploadIcon />}>
            {importLoading ? 'Importando...' : 'Importar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Carga masiva de imágenes por código de producto */}
      <Dialog open={imagenesDialog} onClose={() => setImagenesDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'info.main', color: 'white', fontWeight: 700 }}>
          Cargar Imágenes de Productos
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Cada imagen se asigna al producto cuyo <b>código</b> coincida exactamente con el nombre del archivo
            (sin la extensión). Por ejemplo: <code>12345.jpg</code> se asigna al producto con código <code>12345</code>.
            Las imágenes se redimensionan y comprimen automáticamente al guardarse.
          </Typography>

          <Box
            onDragOver={e => { e.preventDefault(); setImagenesDragOver(true); }}
            onDragLeave={() => setImagenesDragOver(false)}
            onDrop={handleImagenesDrop}
            sx={{
              border: '2px dashed', borderColor: imagenesDragOver ? 'info.main' : 'grey.400',
              borderRadius: 2, p: 3, textAlign: 'center',
              bgcolor: imagenesDragOver ? 'info.lighter' : 'grey.50',
              transition: 'all 0.15s'
            }}
          >
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
              Arrastra aquí archivos o una carpeta completa, o elige una opción:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
              <input type="file" accept="image/*" multiple id="imagenes-file-input" style={{ display: 'none' }}
                onChange={e => { agregarImagenesSeleccionadas(Array.from(e.target.files)); e.target.value = ''; }} />
              <label htmlFor="imagenes-file-input">
                <Button variant="contained" component="span" size="small" startIcon={<CloudUploadIcon />}>
                  Seleccionar archivos
                </Button>
              </label>

              <input type="file" webkitdirectory="" directory="" multiple id="imagenes-folder-input" style={{ display: 'none' }}
                onChange={e => { agregarImagenesSeleccionadas(Array.from(e.target.files)); e.target.value = ''; }} />
              <label htmlFor="imagenes-folder-input">
                <Button variant="outlined" component="span" size="small" startIcon={<CloudUploadIcon />}>
                  Seleccionar carpeta
                </Button>
              </label>
            </Box>

            {imagenesArchivos.length > 0 && (
              <Typography variant="body2" sx={{ mt: 2, color: 'success.dark', fontWeight: 600 }}>
                {imagenesArchivos.length} imagen(es) lista(s) para subir
              </Typography>
            )}
          </Box>

          {imagenesProgreso && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                Subiendo lote {imagenesProgreso.loteActual} de {imagenesProgreso.totalLotes}
                {' — '}{Math.min(imagenesProgreso.procesadas + IMAGENES_LOTE_SIZE, imagenesProgreso.totalArchivos)} de {imagenesProgreso.totalArchivos} imágenes...
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(imagenesProgreso.loteActual / imagenesProgreso.totalLotes) * 100}
              />
            </Box>
          )}

          {imagenesResult && (
            <Box sx={{ mt: 2, p: 2, bgcolor: imagenesResult.ok ? 'success.light' : 'error.light', borderRadius: 1, maxHeight: 220, overflow: 'auto' }}>
              {imagenesResult.ok ? (
                <>
                  <Typography fontWeight={700} color="success.dark">{imagenesLoading ? 'Progreso parcial...' : 'Carga completada'}</Typography>
                  <Typography variant="body2">Total procesadas: <b>{imagenesResult.total}</b></Typography>
                  <Typography variant="body2">Asignadas correctamente: <b>{imagenesResult.asignados}</b></Typography>
                  {imagenesResult.noEncontrados?.length > 0 && (
                    <Typography variant="body2" color="warning.dark" sx={{ mt: 1 }}>
                      Sin producto con ese código ({imagenesResult.noEncontrados.length}): {imagenesResult.noEncontrados.join(', ')}
                    </Typography>
                  )}
                  {imagenesResult.fallidos?.length > 0 && (
                    <Typography variant="body2" color="error.dark" sx={{ mt: 1 }}>
                      Fallidas: {imagenesResult.fallidos.map(f => `${f.codigo} (${f.error})`).join(', ')}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography color="error.dark">Error: {imagenesResult.error}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImagenesDialog(false)}>Cerrar</Button>
          <Button variant="contained" color="info" onClick={handleImagenesUpload}
            disabled={!imagenesArchivos.length || imagenesLoading}
            startIcon={imagenesLoading ? null : <CloudUploadIcon />}>
            {imagenesLoading ? 'Subiendo...' : `Subir ${imagenesArchivos.length || ''} imagen(es)`}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
    </Box>
  );
};

export default Productos;
